/**
 * 타임라인 좌표 모델 — 순수 함수만 둔다.
 *
 * 설계 §1-3의 두 좌표를 분리해 계산한다:
 *
 *   absoluteBeat = sectionOffsetBeats[note] + note.beat   (기준 subdivisions로 정규화)
 *   rowIndex     = floor(absoluteBeat / beatsPerRow)      (화면 위치. BPM 무관)
 *
 * DOM·전역 상태를 참조하지 않는다. `document`/`window`를 쓰지 않으므로 팝아웃 창의
 * `getElementById` 몽키패치(§1-6)에 영향받지 않고, `node --test`로 직접 검증된다.
 *
 * 렌더러는 **읽기 전용**이어야 한다(감사 B-4). 이 모듈의 어떤 함수도 인자로 받은
 * 노트를 수정하지 않는다 — 전부 새 값을 반환한다.
 *
 * `shared/`는 호출만 하고 수정하지 않는다(D-001).
 */

import { beatToTime, timeToBeat } from "@wl/shared/utils.js";

/**
 * 노트의 유효 타이밍 파라미터.
 *
 * `shared/utils.js`의 `getNoteTimingParams`와 같은 폴백 규칙이지만, 여기서는
 * subdivisions 정규화에만 쓰므로 별도로 둔다(계약 함수 시그니처에 의존하지 않는다).
 */
function timingOf(note, baseBpm, baseSubdivisions) {
    return {
        bpm: note.bpm || baseBpm,
        subdivisions: note.subdivisions || baseSubdivisions
    };
}

/**
 * 노트의 `beat`(자기 subdivisions 단위)를 **기준 subdivisions 단위**로 환산한다.
 *
 * §1-3 함정 2: 같은 차트에 4분박·12분박이 섞인다(실측 LT0004-NM: sub=4 한 개 +
 * sub=12 188개). 정규화 없이 beat를 그대로 더하면 12분박 구간에서 행이 밀린다.
 *
 * 시간을 경유하는 이유: `beat`는 "subdivisions 단위의 개수"라 단위가 다르면
 * 직접 비교할 수 없다(D-018). BPM은 초 환산과 역환산에서 상쇄되므로 노트별
 * BPM이 달라도 결과는 subdivisions 비율만으로 결정된다.
 */
export function normalizeBeat(beat, noteSubdivisions, baseSubdivisions) {
    if (noteSubdivisions === baseSubdivisions) return beat; // 왕복 부동소수점 오차 회피
    return beat * (baseSubdivisions / noteSubdivisions);
}

/**
 * 각 노트의 **구간 시작 절대 박자**(기준 subdivisions 단위)를 계산한다.
 *
 * `shared/utils.js`의 `calculateSectionOffsets`와 같은 구조지만 **초가 아니라 박자**를
 * 반환한다(§1-3 함정 1). 초 단위 오프셋은 BPM이 바뀌는 구간에서 박자 축과 어긋나므로
 * 행 인덱스 계산에 쓸 수 없다.
 *
 * `sectionIndex`가 있으면 그것을 기준으로 계산해 **배열 정렬 순서에 독립적**이다.
 * 없으면 배열 순서 기반 레거시 폴백을 쓴다(`calculateSectionOffsets`와 동일한 처우).
 *
 * @returns {number[]} offsets[i] = i번 노트가 속한 구간의 시작 절대 박자
 */
export function calculateSectionOffsetBeats(notes, baseBpm, baseSubdivisions) {
    if (!notes || notes.length === 0) return [];

    // sectionIndex 기반 (정렬 이후에도 안정적)
    if (notes[0].sectionIndex !== undefined) {
        const sectionStartBeats = new Map([[0, 0]]);

        for (const note of notes) {
            if (!note.beatReset) continue;
            const sIdx = note.sectionIndex;
            if (sectionStartBeats.has(sIdx + 1)) continue;

            const prev = sectionStartBeats.get(sIdx) || 0;
            const { subdivisions } = timingOf(note, baseBpm, baseSubdivisions);
            sectionStartBeats.set(
                sIdx + 1,
                prev + normalizeBeat(note.beat, subdivisions, baseSubdivisions)
            );
        }

        return notes.map(n => sectionStartBeats.get(n.sectionIndex) || 0);
    }

    // 레거시 폴백: 배열 순서 기반
    const offsets = new Array(notes.length).fill(0);
    let current = 0;
    for (let i = 0; i < notes.length; i++) {
        offsets[i] = current;
        const note = notes[i];
        if (note.beatReset) {
            const { subdivisions } = timingOf(note, baseBpm, baseSubdivisions);
            current += normalizeBeat(note.beat, subdivisions, baseSubdivisions);
        }
    }
    return offsets;
}

/**
 * 노트별 절대 박자(기준 subdivisions 단위). 구간 오프셋 + 정규화된 자기 beat.
 *
 * @returns {number[]} notes와 1:1 대응
 */
export function calculateAbsoluteBeats(notes, baseBpm, baseSubdivisions) {
    if (!notes || notes.length === 0) return [];
    const offsets = calculateSectionOffsetBeats(notes, baseBpm, baseSubdivisions);
    return notes.map((note, i) => {
        const { subdivisions } = timingOf(note, baseBpm, baseSubdivisions);
        return offsets[i] + normalizeBeat(note.beat, subdivisions, baseSubdivisions);
    });
}

/**
 * 절대 박자 → 행 인덱스. 화면 위치이므로 BPM과 무관하다(§1-3).
 *
 * `beatsPerRow`는 한 행이 담는 **기준 subdivisions 단위 개수**다.
 * 예: baseSubdivisions=16, 한 행 = 1마디(4/4) → beatsPerRow = 16.
 */
export function beatToRow(absoluteBeat, beatsPerRow) {
    return Math.floor(absoluteBeat / beatsPerRow);
}

/**
 * 행 안에서의 위치(0 이상 1 미만). 행 내부 세로 오프셋 계산에 쓴다.
 */
export function beatToRowFraction(absoluteBeat, beatsPerRow) {
    const within = absoluteBeat - beatToRow(absoluteBeat, beatsPerRow) * beatsPerRow;
    return within / beatsPerRow;
}

/**
 * 행 인덱스 + 행 내부 비율 → 절대 박자. `beatToRow`/`beatToRowFraction`의 역함수.
 *
 * 격자 클릭 삽입(§1-2)이 쓰는 역산 경로다. 스냅은 별도 단계이므로 여기서 하지 않는다
 * — 스냅 없는 연속 값을 먼저 얻고 `snapBeat`로 정렬한다.
 */
export function rowToBeat(rowIndex, rowFraction, beatsPerRow) {
    return rowIndex * beatsPerRow + rowFraction * beatsPerRow;
}

/**
 * 절대 박자를 스냅 단위에 정렬한다.
 *
 * `beat`는 **항상 정수로 유지**되어야 하므로(설계 §5 검증 7-1) 결과를 반올림한다.
 * `snapBeats`가 정수면 결과도 정수다.
 */
export function snapBeat(absoluteBeat, snapBeats) {
    if (!snapBeats || snapBeats <= 0) return Math.round(absoluteBeat);
    return Math.round(absoluteBeat / snapBeats) * snapBeats;
}

/**
 * 절대 박자 → 노트에 저장할 `beat` 값.
 *
 * 삽입 지점이 속한 구간의 오프셋을 빼고, 노트가 쓸 subdivisions 단위로 되돌린다.
 * `calculateAbsoluteBeats`의 역방향이다.
 *
 * 반올림하는 이유: `beat`는 정수여야 한다. 기준 subdivisions와 노트 subdivisions가
 * 배수 관계가 아니면 정확히 표현되지 않으므로, 호출부는 `exact`가 false일 때
 * "정확히 표현 불가" 경고를 띄운다(§1-2-D).
 *
 * @returns {{beat:number, exact:boolean}}
 */
export function absoluteBeatToNoteBeat(absoluteBeat, sectionOffsetBeats, noteSubdivisions, baseSubdivisions) {
    const local = absoluteBeat - sectionOffsetBeats;
    const raw = noteSubdivisions === baseSubdivisions
        ? local
        : local * (noteSubdivisions / baseSubdivisions);
    const beat = Math.round(raw);
    return { beat, exact: Math.abs(raw - beat) < 1e-9 };
}

/**
 * subdivisions를 바꿔도 **같은 시간에 머무는** beat를 계산한다(§1-2-D).
 *
 *   newBeat = timeToBeat(beatToTime(beat, bpm, oldSub), bpm, newSub)
 *
 * BPM은 상쇄되지만 계약 함수를 그대로 경유한다 — 두 함수의 왕복 무손실이 이미
 * 검증되어 있고(`contract.test.js`), 공식을 손으로 옮겨 적으면 드리프트한다.
 *
 * 정수로 떨어지지 않으면 `exact: false`를 돌려주어 호출부가 경고하게 한다.
 *
 * @returns {{beat:number, exact:boolean}}
 */
export function retimeBeatForSubdivisions(beat, bpm, oldSubdivisions, newSubdivisions) {
    if (oldSubdivisions === newSubdivisions) return { beat, exact: true };
    const raw = timeToBeat(beatToTime(beat, bpm, oldSubdivisions), bpm, newSubdivisions);
    const rounded = Math.round(raw);
    return { beat: rounded, exact: Math.abs(raw - rounded) < 1e-9 };
}

/**
 * 절대 박자 위치에 노트를 끼워 넣을 **배열 인덱스**를 찾는다.
 *
 * 기존 `addNote`는 "선택 노트 바로 뒤" 또는 "배열 끝"에만 넣는다(설계 §1-2-B 병목 1).
 * 격자 클릭은 임의 위치를 지목하므로 그 자리에 맞는 인덱스를 직접 구해야 한다.
 *
 * **구간(section)을 넘지 않는다.** `sortNotesByTime`은 구간 안에서만 정렬하고
 * `beatReset` 노트를 구간 끝에 고정하므로(`shared/notes.js:294`), 구간 경계를
 * 넘어 삽입하면 정렬이 노트를 되돌려 놓는다. 같은 절대 박자를 갖는 구간이
 * 여럿이면 **가장 이른 구간**을 고른다.
 *
 * 동일 박자에는 **뒤에** 붙인다 — 기존 노트를 밀어내지 않는 쪽이 덜 놀랍다.
 *
 * @param {number[]} absoluteBeats `calculateAbsoluteBeats` 결과
 * @returns {number} splice에 그대로 쓸 인덱스
 */
export function findInsertionIndex(absoluteBeats, notes, targetAbsoluteBeat) {
    if (!notes || notes.length === 0) return 0;

    // 1) 목표 박자를 넘어서는 첫 노트 앞이 기본 자리다(동일 박자에는 뒤에 붙는다).
    let index = notes.length;
    for (let i = 0; i < notes.length; i++) {
        if (absoluteBeats[i] > targetAbsoluteBeat) {
            index = i;
            break;
        }
    }

    // 2) 구간 경계를 넘었으면 되돌린다.
    //
    //    경계에서는 `beatReset` 노트와 다음 구간의 첫 노트가 **같은 절대 박자**를
    //    갖는다(구간 오프셋의 정의가 그렇다). 그래서 1)의 결과가 다음 구간 안쪽으로
    //    넘어가 있을 수 있고, 바로 앞 노트만 봐서는 그것을 알 수 없다.
    //
    //    삽입 지점보다 앞에 있는 `beatReset` 중 **목표 박자 이상**인 것이 있으면
    //    그 앞이 제자리다 — 그래야 `sortNotesByTime`이 노트를 되돌려 놓지 않는다.
    for (let i = 0; i < index; i++) {
        if (notes[i].beatReset && absoluteBeats[i] >= targetAbsoluteBeat) {
            return i;
        }
    }

    return index;
}

/**
 * 삽입 지점에 적용할 **구간 오프셋**을 고른다.
 *
 * 🔴 여기를 틀리면 노트가 구간 하나만큼 어긋난다. 실측한 함정:
 *
 *   notes = [abs 0, abs 32(beatReset), abs 48]   offsets = [0, 0, 32]
 *   절대 40에 삽입 → insertionIndex = 2
 *
 * 이때 `offsets[index - 1]`(= 0)을 쓰면 지역 beat가 40이 되어 노트가 **32박 뒤**로
 * 밀린다. 새 노트는 **뒤 노트와 같은 구간**에 들어가므로 `offsets[index]`가 맞다.
 *
 * 배열 끝에 붙일 때만 앞 노트의 구간을 따른다 — 그 자리에 뒤 노트가 없기 때문이다.
 */
export function sectionOffsetForInsertion(sectionOffsetBeats, insertionIndex, noteCount) {
    if (noteCount === 0) return 0;
    if (insertionIndex >= noteCount) return sectionOffsetBeats[noteCount - 1] || 0;
    return sectionOffsetBeats[insertionIndex] || 0;
}

/**
 * 차트 끝을 나타내는 **종료 노트**의 배열 인덱스. 없으면 -1.
 *
 * 계약상 차트는 `direction`/`none` 노트로 끝나야 한다
 * (`shared/notes.js`의 `ensureFinalDirectionNote`). 그 노트는 경로를 더 늘리지 않고
 * "여기서 끝"만 표시한다.
 *
 * **배열의 마지막 원소로 판정하지 않는다** — 정렬 전이거나 뒤에 노트를 더 찍은
 * 직후에는 종료 노트가 배열 끝이 아닐 수 있다. 대신 조건을 만족하는 것 중
 * **절대 박자가 가장 큰 것**을 고른다(그것이 실제 끝이다).
 *
 * 시작 노트(beat 0 direction)와 겹치지 않게 하려면 호출부가 `isChartStartNote`로
 * 걸러야 한다 — 노트가 하나뿐인 차트에서 시작=끝이 되는 것을 막기 위해서다.
 */
export function findChartEndNoteIndex(notes, absoluteBeats) {
    let best = -1;
    let bestBeat = -Infinity;

    for (let i = 0; i < notes.length; i++) {
        const n = notes[i];
        if (n.type !== "direction") continue;
        if ((n.direction || "none") !== "none") continue;

        const beat = absoluteBeats[i];
        if (beat > bestBeat) {
            bestBeat = beat;
            best = i;
        }
    }

    return best;
}

/**
 * 종료 노트가 있어야 할 **최소 절대 박자**.
 *
 * 종료 노트를 제외한 나머지 노트 중 가장 늦게 끝나는 지점이다. 롱노트는
 * `longTime`만큼 뒤에서 끝나므로 그 길이를 더한다(내부 shape 기준 **박자 단위**).
 *
 * 이 값보다 앞으로는 종료 노트를 끌 수 없다 — 그러면 차트가 노트 위에서 끊긴다.
 */
export function minimumChartEndBeat(notes, absoluteBeats, endIndex, baseSubdivisions) {
    let max = 0;

    for (let i = 0; i < notes.length; i++) {
        if (i === endIndex) continue;

        const n = notes[i];
        let end = absoluteBeats[i];

        if (n.isLong && n.longTime > 0) {
            const subs = n.subdivisions || baseSubdivisions;
            end += normalizeBeat(n.longTime, subs, baseSubdivisions);
        }

        if (end > max) max = end;
    }

    return max;
}

/**
 * 렌더러가 쓰는 행 배치 정보를 한 번에 만든다.
 *
 * **인자로 받은 노트를 수정하지 않는다** — 인덱스 참조만 담은 새 객체를 낸다.
 * 렌더가 모델을 건드리는 B-4 함정을 구조적으로 막기 위해서다.
 *
 * @returns {{rowCount:number, placements:Array<{index:number, absoluteBeat:number,
 *           row:number, rowFraction:number}>}}
 */
export function buildTimelineLayout(notes, baseBpm, baseSubdivisions, beatsPerRow) {
    const absoluteBeats = calculateAbsoluteBeats(notes, baseBpm, baseSubdivisions);

    const placements = absoluteBeats.map((absoluteBeat, index) => ({
        index,
        absoluteBeat,
        row: beatToRow(absoluteBeat, beatsPerRow),
        rowFraction: beatToRowFraction(absoluteBeat, beatsPerRow)
    }));

    // 노트가 없어도 격자는 최소 1행을 그린다.
    const maxRow = placements.reduce((m, p) => Math.max(m, p.row), 0);
    return { rowCount: placements.length ? maxRow + 1 : 1, placements };
}

/**
 * 드래그를 **자기 구간 안으로** 가둔다.
 *
 * 🔴 **왜 경계를 넘을 수 없는가**: `sectionIndex`는 **배열 순서에서 파생된다**
 * (`shared/utils.js`의 `recomputeSectionIndices`). 그런데 드래그는 배열 위치를
 * 바꾸지 않으므로(§1-2-C), 노트를 다른 구간의 박자로 옮겨도 `sectionIndex`는
 * 그대로 0이고 `invalidatePathCache()`가 매 프레임 그 값을 다시 덮어쓴다.
 * 결과적으로 지역 beat만 새 구간 기준이 되고 오프셋은 옛 구간이라,
 * **노트가 옛 구간의 처음 쪽으로 되돌아간다**(사용자 보고 2026-07-30).
 *
 *   구간0 = 0~31, 구간1 = 32~ · 구간0의 노트를 절대 40으로 드래그
 *     지역 beat = 40 - 32 = 8   그러나 sectionIndex는 여전히 0
 *     → 실제 절대 = 0 + 8 = 8   ← 구간0의 8박으로 점프 🔴
 *
 * 진짜로 구간을 옮기려면 **배열에서 재배치**해야 하는데, 그러면 인덱스가 바뀌어
 * 선택·undo와 충돌한다. 그래서 `beatReset` 드래그·시작 노트 드래그와 **같은 처우**로
 * 막는다(사용자 결정 2026-07-30). 구간 변경은 표/인스펙터에서 한다.
 *
 * @param {number} targetAbsoluteBeat 커서가 가리키는 절대 박자
 * @param {number} sectionOffsetBeats 그 노트가 **속한** 구간의 시작 절대 박자
 * @param {number[]} starts `sectionStartBeats`의 결과
 * @returns {number} 자기 구간 안으로 clamp된 절대 박자
 */
export function clampBeatToSection(targetAbsoluteBeat, sectionOffsetBeats, starts) {
    if (targetAbsoluteBeat < sectionOffsetBeats) return sectionOffsetBeats;

    // 다음 구간의 시작 = 자기 구간의 끝(그 박자는 이미 다음 구간 것이다).
    const next = (starts || []).find(s => s > sectionOffsetBeats);
    if (next !== undefined && targetAbsoluteBeat >= next) return next - 1;

    return targetAbsoluteBeat;
}

/**
 * BPM을 **선언하는** 노트 타입. Tab 계열은 여기 없다 — 상속만 받는다.
 *
 * 🔴 `script.js`의 `BPM_EDITABLE_TYPES`와 **같은 목록이어야 한다.** 갈리면 드래그가
 * 참조하는 선언 노트와 표·인스펙터가 편집하는 노트가 어긋난다. 테스트로 잠근다.
 */
export const BPM_DECLARING_TYPES = ["direction", "longdirection", "both", "longboth", "node"];

/**
 * `targetAbsoluteBeat` 위치를 **시간 축에서** 지배하는 BPM 선언을 찾는다.
 *
 * 🔴 **왜 배열 순서가 아니라 절대 박자로 찾는가**: 타임라인 드래그는 배열 위치를
 * 바꾸지 않는다(자동 정렬 없음 — §1-2-C). 그래서 노트를 다른 구간으로 끌어도
 * 배열상으로는 **옛 자리에 남아 있고**, `updateTabNotesInheritance`처럼 인덱스를
 * 훑는 방식은 **옛 구간의 BPM을 계속 준다**(보고된 증상).
 *
 * 자기 자신(`excludeIndex`)은 후보에서 뺀다 — Tab 노트에는 상속값이 이미 박혀
 * 있어 포함하면 자기 옛 값을 되돌려 받는다.
 *
 * 동률(같은 절대 박자)이면 배열에서 **뒤에 있는** 선언이 이긴다 — `calculateNoteTime`이
 * 같은 beat의 BPM 변경을 나중 것으로 덮는 것과 같은 방향이다.
 *
 * @param {Array} notes
 * @param {number[]} absoluteBeats `calculateAbsoluteBeats`의 결과
 * @param {number} targetAbsoluteBeat 상속받을 위치
 * @param {number} excludeIndex 제외할 노트 인덱스(자기 자신). 없으면 -1
 * @returns {{bpm:number, subdivisions:number, index:number}|null}
 *          지배하는 선언이 없으면(=앞에 선언 노트가 없음) `null` — 호출부가 전역값을 쓴다.
 */
export function governingBpmDeclaration(notes, absoluteBeats, targetAbsoluteBeat, excludeIndex = -1) {
    let best = null;

    for (let i = 0; i < notes.length; i++) {
        if (i === excludeIndex) continue;
        const note = notes[i];
        if (!note || !BPM_DECLARING_TYPES.includes(note.type)) continue;
        if (note.bpm === undefined && note.subdivisions === undefined) continue;

        const beat = absoluteBeats[i];
        if (beat > targetAbsoluteBeat) continue;          // 아직 도달하지 않은 선언

        // 동률이면 뒤 인덱스가 이긴다(>= 로 갱신).
        if (best === null || beat > best.beat || (beat === best.beat && i > best.index)) {
            best = { beat, index: i, bpm: note.bpm, subdivisions: note.subdivisions };
        }
    }

    if (best === null) return null;
    return { bpm: best.bpm, subdivisions: best.subdivisions, index: best.index };
}

/**
 * 각 구간의 **시작 절대 박자**를 오름차순으로 낸다(중복 제거).
 *
 * 구간 경계는 `beatReset` 노트 **다음**부터다. 행 라벨을 구간 기준으로 다시 세거나
 * 격자에 경계선을 그을 때 쓴다.
 *
 * @returns {number[]} 항상 0으로 시작한다(첫 구간).
 */
export function sectionStartBeats(notes, baseBpm, baseSubdivisions) {
    const offsets = calculateSectionOffsetBeats(notes, baseBpm, baseSubdivisions);
    const seen = new Set([0]);
    for (const o of offsets) seen.add(o);
    return [...seen].sort((a, b) => a - b);
}

/**
 * 행 라벨을 만든다. **구간이 나뉜 차트에서 절대 박자만 보여주면 표의 Beat와 어긋난다**
 * — `beatReset` 뒤의 노트는 지역 beat가 0부터 다시 시작하기 때문이다.
 *
 * 구간이 하나뿐이면(대부분의 차트) 지금까지처럼 절대 박자 하나만 낸다.
 * 구간이 여럿이면 `구간:지역박자` 형태로 낸다 — 표의 Beat 셀과 같은 축이다.
 *
 * ⚠️ **행 위치 자체는 BPM과 무관하다**(normalizeBeat 주석 참조 — BPM은 초 환산과
 * 역환산에서 상쇄된다). BPM이 바뀌어도 노트는 제자리에 찍히며, 이 함수가 고치는 것은
 * **라벨의 의미**지 위치가 아니다. 실제 시간이 필요하면 드래그 툴팁의 `Time`을 본다.
 *
 * @param {number} rowBeat  그 행의 시작 절대 박자 (= row * beatsPerRow)
 * @param {number[]} starts `sectionStartBeats`의 결과
 * @returns {{text:string, sectionIndex:number, localBeat:number, isSectionStart:boolean}}
 */
export function rowLabelParts(rowBeat, starts) {
    if (!starts || starts.length <= 1) {
        return { text: formatRowBeat(rowBeat), sectionIndex: 0, localBeat: rowBeat, isSectionStart: rowBeat === 0 };
    }

    // 이 행이 속한 구간 = rowBeat 이하인 마지막 시작점.
    let sectionIndex = 0;
    for (let i = 0; i < starts.length; i++) {
        if (starts[i] <= rowBeat) sectionIndex = i;
        else break;
    }

    const localBeat = rowBeat - starts[sectionIndex];
    return {
        text: `${sectionIndex}:${formatRowBeat(localBeat)}`,
        sectionIndex,
        localBeat,
        isSectionStart: localBeat === 0
    };
}

/**
 * 행 라벨의 숫자 표기.
 *
 * 행 단위가 1박보다 작으면(1/4박·1/2박) `row * beatsPerRow`가 **소수**가 된다
 * (예: sub=12에서 1/4박 = 3박이지만, sub=16이면 4박 → 정수. sub=12에서 1/2박 = 6박).
 * 나누어떨어지지 않는 조합에서는 `12.5` 같은 값이 나오므로 **소수점 한 자리까지만**
 * 보이고 정수는 그대로 낸다 — 라벨이 `12.333333`으로 번지는 것을 막는다.
 *
 * ⚠️ 반환값은 **표시용 문자열**이다. `localBeat`·`rowBeat`는 계산에 쓰이므로
 * 반올림하지 않고 원본 숫자를 그대로 돌려준다(호출부가 좌표 계산에 쓴다).
 */
function formatRowBeat(beat) {
    return Number.isInteger(beat) ? String(beat) : String(Math.round(beat * 10) / 10);
}

// ── 이벤트 열 ────────────────────────────────────────────────────────────
//
// 🔴 **`eventTime`은 노트와 다른 시간축이다.** preDelay를 적용한 **후**의 축
// (= 노트의 `finalTime`)이며, 에디터가 노트에 쓰는 `originalTime`은 적용 전이다.
// 근거 3가지가 독립적으로 일치한다(실측 2026-07-31):
//
//   - `handlePreDelayChange`가 preDelay 변경분만큼 **모든 eventTime을 이동**시킨다.
//     노트는 이동시키지 않는다(script.js).
//   - `checkEventExecution(elapsedTime)` — elapsedTime은 재생 wall-clock(preDelay 포함).
//   - 이벤트 리스트의 Time 입력은 eventTime을 **날것 그대로** 읽고 쓴다.
//     signature와 달리 JSON 경계에서 변환하지 않는다.
//
// 그래서 격자(박자 축)에 얹으려면 **preDelay를 먼저 빼야 한다.**
// `playheadOffsetPx`가 재생 헤드에 하는 보정과 같은 이유·같은 방향이다.

/**
 * 이벤트 시각(초, preDelay 후) → 절대 박자(기준 subdivisions 단위).
 *
 * `playheadOffsetPx`와 **같은 식**을 쓴다 — 둘이 갈리면 재생 헤드가 지나가는 순간과
 * 이벤트 마커의 위치가 어긋나 보인다.
 *
 * ⚠️ **전역 BPM만 쓴다.** 변속 차트에서는 뒤로 갈수록 실제 위치와 어긋날 수 있다.
 * 재생 헤드가 이미 같은 한계를 갖고 있어(`playheadOffsetPx`) 둘은 **함께 어긋난다**
 * — 즉 헤드와 마커의 상대 관계는 유지된다. 정확한 변속 대응은 별도 안건이다.
 *
 * @returns {number} 절대 박자. preDelay보다 이른 이벤트는 **음수**가 된다.
 */
export function eventTimeToAbsoluteBeat(eventTime, preDelaySeconds, bpm, subdivisions) {
    const originalTime = eventTime - preDelaySeconds;
    return (originalTime * bpm * subdivisions) / 60;
}

/**
 * 절대 박자 → 이벤트 시각(초, preDelay 후). `eventTimeToAbsoluteBeat`의 역함수.
 *
 * 드래그로 이벤트를 옮길 때 쓴다 — 격자에서 얻은 박자를 다시 `eventTime` 축으로
 * 되돌려야 저장할 수 있다.
 */
export function absoluteBeatToEventTime(absoluteBeat, preDelaySeconds, bpm, subdivisions) {
    const originalTime = (absoluteBeat * 60) / (bpm * subdivisions);
    return originalTime + preDelaySeconds;
}

/**
 * 이벤트를 **같은 시각끼리 묶어** 행에 배치한다.
 *
 * 그룹으로 묶는 이유(사용자 결정): 같은 시각의 이벤트가 실제로 존재한다 —
 * 실측 `real-chart-LT0004-NM.json`은 이벤트 6개가 **3쌍**(0.11 · 9.385 · 9.485)으로
 * 겹친다. 각각 마커를 그리면 완전히 포개져 개수를 셀 수 없다.
 *
 * 🔴 **preDelay보다 이른 이벤트는 0행에 clamp한다**(사용자 결정 2026-07-31).
 * 위 실측 차트가 바로 그 경우다(preDelay 2.585초 · 이벤트 2개가 0.11초 → 약 -2.475초).
 * 잘라내면 **이벤트가 눈에 띄지 않게 사라지므로** 0행 맨 위에 모아 `clamped`로
 * 표시하고, 호출부가 "시작 전"임을 알린다. 격자를 음수까지 늘리지 않는 이유는
 * 행 인덱스·가상 스크롤·스페이서 높이가 전부 0 기준이기 때문이다.
 *
 * 부동소수점 시각을 그대로 키로 쓰지 않는다 — `9.385000000000002` 같은 값이 실제로
 * 들어 있어 같은 시각이 갈라진다. **박자로 환산한 뒤 반올림**해 묶는다.
 *
 * @param {Array} events `getAllEvents()` 결과. **수정하지 않는다.**
 * @returns {Array<{absoluteBeat:number, row:number, rowFraction:number,
 *                  clamped:boolean, indices:number[], events:Array}>}
 */
export function buildEventPlacements(events, preDelaySeconds, bpm, subdivisions, beatsPerRow) {
    if (!events || events.length === 0) return [];

    const groups = new Map();

    events.forEach((event, index) => {
        if (!event || typeof event.eventTime !== "number" || !Number.isFinite(event.eventTime)) return;

        const raw = eventTimeToAbsoluteBeat(event.eventTime, preDelaySeconds, bpm, subdivisions);

        // 음수는 0행으로 끌어올린다. clamped를 남겨 호출부가 "시작 전"을 표시한다.
        const clamped = raw < 0;
        const absoluteBeat = clamped ? 0 : raw;

        // 1/1000박까지만 같게 본다 — 부동소수점 잔차로 같은 시각이 갈라지는 것을 막는다.
        const key = Math.round(absoluteBeat * 1000);

        if (!groups.has(key)) {
            groups.set(key, {
                absoluteBeat,
                row: beatToRow(absoluteBeat, beatsPerRow),
                rowFraction: beatToRowFraction(absoluteBeat, beatsPerRow),
                clamped,
                indices: [],
                events: []
            });
        }

        const g = groups.get(key);
        // 한 그룹 안에서 clamp된 것과 아닌 것이 섞이지는 않는다(음수는 전부 0으로 모인다).
        // 다만 정확히 0박인 이벤트가 함께 들어올 수 있으므로 하나라도 clamped면 표시한다.
        if (clamped) g.clamped = true;
        g.indices.push(index);
        g.events.push(event);
    });

    return [...groups.values()].sort((a, b) => a.absoluteBeat - b.absoluteBeat);
}

/**
 * 이벤트 마커에 띄울 툴팁 문구.
 *
 * 합의된 표시 범위는 **이벤트 종류 + 시작 시간**뿐이다 — 전체 편집은 이벤트 리스트
 * 탭이 맡는다. 그룹이면 종류를 줄바꿈으로 나열한다.
 *
 * `eventTime`을 **그대로** 보여준다(변환하지 않는다). 이벤트 리스트의 Time 입력과
 * 같은 값이어야 대조할 수 있기 때문이다 — 박자로 바꿔 보여주면 두 화면이 갈린다.
 */
export function eventTooltipText(group) {
    if (!group || !group.events || group.events.length === 0) return "";

    const lines = group.events.map(e => {
        const id = e.eventId ? `-${e.eventId}` : "";
        return `${e.eventType || "?"}${id} · ${e.eventTime}s`;
    });

    if (group.clamped) {
        lines.push("⚠ preDelay보다 이른 이벤트 (0행에 표시)");
    }

    return lines.join("\n");
}
